/**
 * B397 leg (3) — the merged cart & pay page's pure derivations (73:2).
 *
 * The frame's line item reads "2 × Adult · Fri 15 · 10:30" — count × the
 * CATALOG's type name · the departure. Every part is a read's: the type
 * name is the catalog's (a variant it no longer names degrades to the
 * product's name, never the wire's id — B238/B239), the departure is the
 * same label the confirmation uses, and an absent part leaves no dangling
 * separator. The hold words are the hold's own clock restated in buyer
 * time (mm:ss); an expired or absent hold has NO words, because the chip's
 * absence is the honest face (§10).
 */
import type { Money } from "./money.js";
import { seatsLeft, type AvailabilityMap } from "./browse.js";
import type { CartLine, ProductSummary } from "../ui/screens.js";

export interface CartCardLine {
  readonly lineId: string;
  /** "2 × Adult · Fri 15 · 10:30" — the frame's sentence. */
  readonly sentence: string;
  /** The line's display amount; absent when the wire carries none. */
  readonly amount?: Money;
  /** B391 — the stepper the line wears (a Pathik ruling the frame does not draw; recorded). */
  readonly quantity: number;
  /** The catalog's type name, for the stepper's spoken acts ("Fewer Adult"). */
  readonly typeName: string;
  /** B545 (370-2) — the line CARD's facts: the product's name… */
  readonly productName?: string;
  /** …the departure's words (absent for an untimed line)… */
  readonly when?: string;
  /** …"2 × Adult" (count × the class, qualifier dropped)… */
  readonly qtyWords?: string;
  /** …and the card's first photo, when the catalog carries one. */
  readonly photo?: { readonly url: string; readonly alt?: string };
}

export function cartCardLines(
  lines: readonly CartLine[],
  products: readonly ProductSummary[],
  /**
   * The departure's label for a line, injected: the container prefers the
   * session's own picked-slot label and falls back to the wire's slotStart
   * (a reloaded cart still names its crossing — the dateFaces wall).
   */
  labelOf: (line: CartLine) => string | undefined,
): readonly CartCardLine[] {
  /* A cart spanning products must say WHICH product each type belongs to —
     "2 × Adult" twice, from two sailings' products, is indistinguishable.
     One product (the frame's own demo) keeps the frame's short sentence. */
  const multiProduct = new Set(lines.map((l) => l.productId)).size > 1;
  return lines.map((l) => {
    const product = products.find((p) => p.productId === l.productId);
    const variantName =
      l.variantId === undefined ? undefined : product?.variants.find((v) => v.variantId === l.variantId)?.name;
    const typeName = variantName ?? product?.name ?? l.productName;
    const productName = product?.name ?? l.productName;
    const slot = labelOf(l);
    /* B403 (73:2, the copy wall): the line drops the type's parenthetical
       qualifier — "1 × Child · Fri 15 · 10:30", the card's own row already
       states "(2–12)"; the stepper's spoken acts keep the full name. */
    const lineName = typeName.replace(/\s*\(.*\)\s*$/u, "").trim();
    const sentence = [
      `${l.quantity} × ${lineName}`,
      ...(multiProduct && typeName !== productName ? [productName] : []),
      ...(slot === undefined ? [] : [slot]),
    ].join(" · ");
    const photo = product?.presentation?.photos?.[0];
    return {
      lineId: l.lineId,
      sentence,
      quantity: l.quantity,
      typeName,
      productName,
      qtyWords: `${l.quantity} × ${lineName}`,
      ...(slot === undefined ? {} : { when: slot }),
      ...(photo === undefined ? {} : { photo: { url: photo.url, ...(photo.alt !== undefined ? { alt: photo.alt } : {}) } }),
      ...(l.amount === undefined ? {} : { amount: l.amount }),
    };
  });
}

/** "Held for 9:42" — undefined at or past expiry (the chip leaves the page). */
export function holdWords(msLeft: number): string | undefined {
  if (!Number.isFinite(msLeft) || msLeft <= 0) return undefined;
  const totalSeconds = Math.floor(msLeft / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `Held for ${minutes}:${String(seconds).padStart(2, "0")}`;
}


export interface CartAvailabilityHint {
  readonly lineIds: readonly string[];
  readonly message: string;
}

/**
 * B523 — Cart re-reads availability on open (the item's rule: "the buyer
 * never learns at Pay what they could have learned at Cart"). Per slot the
 * cart rides: the seats wanted are the SUM of its lines (they compete for
 * the same seats), the wall is `seatsLeft` (the tightest pool). A slot not
 * in the map claims nothing — not-read-yet is never "none" (§10, the
 * seatsLeft rule). The hint lands on every line riding the short slot, and
 * any hint walls Continue (CartPayView's existing gate).
 */
export function cartAvailabilityHints(
  lines: readonly CartLine[],
  availability: AvailabilityMap,
  labelOf: (line: CartLine) => string | undefined,
): readonly CartAvailabilityHint[] {
  const bySlot = new Map<string, { wanted: number; lineIds: string[]; label: string | undefined }>();
  for (const line of lines) {
    if (line.slotId === undefined) continue;
    const entry = bySlot.get(line.slotId) ?? { wanted: 0, lineIds: [], label: undefined };
    entry.wanted += line.quantity;
    entry.lineIds.push(line.lineId);
    entry.label = entry.label ?? labelOf(line);
    bySlot.set(line.slotId, entry);
  }
  const hints: CartAvailabilityHint[] = [];
  for (const [slotId, entry] of bySlot) {
    const pools = availability[slotId];
    if (pools === undefined) continue;
    const left = seatsLeft(pools);
    if (left === undefined || left >= entry.wanted) continue;
    const where = entry.label === undefined ? "for this time" : `at ${entry.label}`; // generic on purpose: industry nouns ride vocabulary packs (B126)
    hints.push({
      lineIds: entry.lineIds,
      message: left === 0 ? `Sold out ${where}.` : `Only ${left} left ${where}.`,
    });
  }
  return hints;
}

/** B629 (c) — one summary row a buyer reads between the promotions and the Total. */
export interface ChargeRow {
  readonly key: string;
  readonly label: string;
  readonly amount: Money;
}

/**
 * B629 (c) — the wire's charges in the order the buyer reads them: each fee
 * they pay, named as the tenant named it, then the (exclusive) tax. Both
 * ride the totals only when they charge, so an untaxed, fee-free cart draws
 * no row at all.
 */
export function chargeRows(totals: {
  readonly fees?: readonly { readonly feeId: string; readonly label: string; readonly amount: Money }[] | undefined;
  readonly taxTotal?: Money | undefined;
}): ChargeRow[] {
  return [
    ...(totals.fees ?? []).map((f) => ({ key: `fee-${f.feeId}`, label: f.label, amount: f.amount })),
    ...(totals.taxTotal !== undefined ? [{ key: "tax", label: "Tax", amount: totals.taxTotal }] : []),
  ];
}
